import { ChartConfig } from '@/components/ui/chart';

interface ChartDataItem {
  key: string;
  value: number;
  fill: string;
}

interface AccountingCenter {
  acmev?: number;
  acmev_used?: number;
  acoh?: number;
  acoh_used?: number;
  actev?: number;
  acrt?: number;
  acrt_achieved?: number;
}

const defaultBtevChartData = (defaultColor: string) => [
  { key: 'remaining', value: 100, fill: defaultColor },
  { key: 'acmevSpent', value: 0, fill: '#FF2D55' },
  { key: 'acohSpent', value: 0, fill: '#FF9500' }
];

const calculateBtevChartData = (acCenter: AccountingCenter | undefined, defaultColor = '#32ADE6') => {
  if (!acCenter) {
    return {
      btevChartData: defaultBtevChartData(defaultColor),
      btevRemainingPercent: 100,
      btevRemainingAmount: 0
    };
  }

  const acmevUsed = acCenter.acmev_used ?? 0;
  const acmevRemaining = acCenter.acmev ?? 0;
  const acohTotal = acCenter.acoh ?? 0;
  const acohUsed = acCenter.acoh_used ?? 0;
  const acohRemaining = Math.max(0, acohTotal - acohUsed);
  const btevTotal = acCenter.actev ?? acmevRemaining + acmevUsed + acohTotal;
  const btevRemainingAmount = acmevRemaining + acohRemaining;

  if (btevTotal <= 0) {
    return {
      btevChartData: defaultBtevChartData(defaultColor),
      btevRemainingPercent: 100,
      btevRemainingAmount: 0
    };
  }

  const btevRemainingPercent = Math.round((btevRemainingAmount / btevTotal) * 100);

  return {
    btevChartData: [
      { key: 'remaining', value: btevRemainingPercent, fill: defaultColor },
      { key: 'acmevSpent', value: Math.round((acmevUsed / btevTotal) * 100), fill: '#FF2D55' },
      { key: 'acohSpent', value: Math.round((acohUsed / btevTotal) * 100), fill: '#FF9500' }
    ],
    btevRemainingPercent,
    btevRemainingAmount
  };
};

export interface ChartCalculationResult {
  acmevChartData: ChartDataItem[];
  acrtChartData: ChartDataItem[];
  btevChartData: ChartDataItem[];
  btevRemainingPercent: number;
  btevRemainingAmount: number;
}

export const calculateChartData = (
  acCenter: AccountingCenter | undefined,
  defaultColor = '#32ADE6'
): ChartCalculationResult => {
  if (!acCenter) {
    return {
      acmevChartData: [{ key: 'remaining', value: 100, fill: defaultColor }],
      acrtChartData: [{ key: 'target', value: 100, fill: defaultColor }],
      ...calculateBtevChartData(undefined, defaultColor)
    };
  }

  // Calculate percentages for ACMEV (Accounting Center Maximum Expense Value)
  let acmevData = [];
  if (acCenter.acmev !== undefined) {
    const acmevTotal = (acCenter.acmev || 0) + (acCenter.acmev_used || 0);
    const acmevUsed = acCenter.acmev_used || 0;
    const acmevRemaining = acCenter.acmev;

    // Only add remaining if there's a total budget value
    if (acmevTotal > 0) {
      const acmevRemainingPercent = Math.round((acmevRemaining / acmevTotal) * 100);
      acmevData.push({ key: 'remaining', value: acmevRemainingPercent, fill: defaultColor });

      const acmevUsedPercent = Math.round((acmevUsed / acmevTotal) * 100);
      acmevData.push({ key: 'spent', value: acmevUsedPercent, fill: '#FF2D55' });
    } else {
      // Default value if acmev is 0
      acmevData.push({ key: 'remaining', value: 100, fill: defaultColor });
    }
  } else {
    // Default value if acmev is not defined
    acmevData.push({ key: 'remaining', value: 100, fill: defaultColor });
  }

  // Calculate percentages for ACRT (Accounting Center Revenue Target)
  let acrtData = [];
  if (acCenter.acrt !== undefined) {
    const acrtTotal = acCenter.acrt || 0;
    const acrtAchieved = acCenter.acrt_achieved || 0;

    // Only calculate percentages if there's a target
    if (acrtTotal > 0) {
      const acrtAchievedPercent = Math.round((acrtAchieved / acrtTotal) * 100);

      // Add target (always 100%)
      acrtData.push({ key: 'target', value: 100 - acrtAchievedPercent, fill: defaultColor });
      acrtData.push({ key: 'achieved', value: acrtAchievedPercent, fill: '#34C759' });
    } else {
      // Default value if acrt is 0
      acrtData.push({ key: 'target', value: 100, fill: defaultColor });
    }
  } else {
    // Default value if acrt is not defined
    acrtData.push({ key: 'target', value: 100, fill: defaultColor });
  }

  return {
    acmevChartData: acmevData,
    acrtChartData: acrtData,
    ...calculateBtevChartData(acCenter, defaultColor)
  };
};

export const getBtevChartConfig = (variant: 'primary' | 'secondary' = 'secondary') => {
  const primaryColor = variant === 'primary' ? '#7663EB' : '#32ADE6';

  return {
    remaining: {
      label: 'Remaining ACMEV',
      color: primaryColor
    },
    acmevSpent: {
      label: 'ACMEV Spent',
      color: '#FF2D55'
    },
    acohSpent: {
      label: 'ACOH Spent',
      color: '#FF9500'
    }
  } satisfies ChartConfig;
};

export const getChartConfigs = (variant: 'primary' | 'secondary' = 'primary') => {
  const primaryColor = variant === 'primary' ? '#7663EB' : '#32ADE6';
  const acmevChartConfig = {
    remaining: {
      label: 'Remaining Budget',
      color: primaryColor
    },
    spent: {
      label: 'Budget Spent',
      color: '#FF2D55'
    }
  } satisfies ChartConfig;

  const acrtChartConfig = {
    target: {
      label: 'Revenue Target',
      color: primaryColor
    },
    achieved: {
      label: 'Achieved Revenue',
      color: '#34C759'
    }
  } satisfies ChartConfig;

  return { acmevChartConfig, acrtChartConfig };
};
